import { Row, Col } from "antd";
import { Key, useState } from "react";
import { useTranslation } from "react-i18next";

const baseUrl = "https://cleanskyenergy.com";

const menuSections = (t: any) => [
  {
    title: t("Plans"),
    links: [
      {
        title: t("Electricity for Home"),
        url: `${baseUrl}/residential-electricity`,
      },
      {
        title: t("Electricity for Business"),
        url: `${baseUrl}/business-electricity`,
      },
      {
        title: t("Gas for Home"),
        url: `${baseUrl}/residential-natural-gas`,
      },
      {
        title: t("Gas for Business"),
        url: `${baseUrl}/commercial-natural-gas`,
      },
    ],
  },
  {
    title: t("About Us"),
    links: [
      { title: t("FAQs"), url: `${baseUrl}/faq` },
      { title: t("Careers"), url: `${baseUrl}/careers` },
      { title: t("Reviews"), url: `${baseUrl}/reviews` },
      {
        title: t("CleanSky Cares"),
        url: "https://cleanskyenergy.com/cleanskycares/?_gl=1*e1l8pd*_gcl_au*MTYwMTYzNzE0Ni4xNzQ3MTIxOTQ3*_ga*MTA0NDkyNjkyNS4xNzQ3MTIxOTQy*_ga_H31ZYZC5HK*czE3NDk3MjIwNjIkbzg3JGcxJHQxNzQ5NzIyMTM4JGo2MCRsMCRoMA..",
      },
      {
        title: t("Do Not Call Policy"),
        url: "https://cleanskyenergy.com/wp-content/uploads/2024/05/Do-Not-Contact-Policy-DNC.pdf?_gl=1*5wxbzn*_gcl_au*MTYwMTYzNzE0Ni4xNzQ3MTIxOTQ3*_ga*MTA0NDkyNjkyNS4xNzQ3MTIxOTQy*_ga_H31ZYZC5HK*czE3NDk3MjIwNjIkbzg3JGcwJHQxNzQ5NzIyMDYyJGo2MCRsMCRoMA..",
      },
      { title: t("Privacy Policy"), url: `${baseUrl}/terms-of-use` },
    ],
  },
  {
    title: t("Resources"),
    links: [
      { title: t("Blog"), url: `${baseUrl}/blog` },
      { title: t("Outage Numbers"), url: `${baseUrl}/outage-phone-numbers` },
      { title: t("Texas Electricity"), url: `${baseUrl}/texas-electricity` },
      {
        title: t("Variable Rate History"),
        url: `${baseUrl}/variable-rate-history`,
      },
      {
        title: "Environmental Disclosures",
        url: "https://cleanskyenergy.com/environmental-disclosures/?_gl=1*pp90td*_gcl_au*MTYwMTYzNzE0Ni4xNzQ3MTIxOTQ3*_ga*MTA0NDkyNjkyNS4xNzQ3MTIxOTQy*_ga_H31ZYZC5HK*czE3NDk3MjIwNjIkbzg3JGcxJHQxNzQ5NzIyMTM4JGo2MCRsMCRoMA..",
      },
      { title: t("Sitemap"), url: `${baseUrl}/sitemap` },
    ],
  },
];

const licenseContent = (t: any) => ({
  DC: t(
    "CleanSky Energy is a licensed supplier of electricity and natural gas supply in the District Of Columbia. D.C. Lic#- Elec EA-2017-15, Gas GA-2017-09"
  ),
  IL: t(
    "CleanSky Energy is certified to provide electricity in the state of Illinois. ICC ARES Certificate: 18-1266"
  ),
  MA: t(
    "CleanSky Energy is a licensed competitive supplier in the state of Massachusetts. Mass DPU License: CS-177"
  ),
  MD: t(
    "CleanSky Energy is a licensed supplier in the state of Maryland. Electric License: IR-4126, Gas License: IR-4127"
  ),
  NJ: t(
    "CleanSky Energy is a licensed Third-Party Supplier (“TPS”) for electricity and natural gas in the State of New Jersey. NJ License Number - ESL-0255 (Power) / GSL-0223 (Gas)."
  ),
  OH: t(
    "CleanSky Energy is certified to provide electricity and natural gas supply in the State of Ohio. PUCO Certificates: Electric: 13-700E, Natural Gas: Cert # 15-485G"
  ),
  PA: t(
    "CleanSky Energy operates as an Electric Generation Supplier (“EGS”) and Natural Gas Supplier (“NGS”) in the state of Pennsylvania. Electric License: A-2016-2530633, Gas License: A-2008-2054272."
  ),
  TX: t(
    "CleanSky Energy is a certified retail energy provider in the state of Texas. PUCT# 10268"
  ),
  DE: t(
    "CleanSky Energy is a licensed retail energy provider in the state of Delaware. Order #10080"
  ),
  NH: t(""),
});

const contactSection = (t: any) => ({
  title: t("Contact"),
  customerServiceHours: {
    label: t("serviceHours"),
    value: t("serviceHoursText"),
  },
  mainPhone: {
    label: t("mainPhone"),
    value: "1-(888) 355-6205",
  },
  forTexasCustomers: {
    label: t("texasPhone"),
    value: "1-(888) 733-5557",
  },
});

const Footer = ({ isTx }: { isTx: boolean }) => {
  const { t } = useTranslation();
  const [selectedLicense, setSelectedLicense] =
    useState<keyof ReturnType<typeof licenseContent>>("DC");
  return (
    <footer
      className={`text-white`}
      style={{
        backgroundColor: "#253f66",
        marginTop: "auto",
        padding: "4rem 0",
      }}
    >
      <div className="container col-md-10 mx-auto">
        <Row gutter={[32, 32]}>
          <Col xs={24} lg={8}>
            <a href={baseUrl}>
              <img
                src="/images/logo-footer.png"
                alt="logo"
                width={200}
                height={50}
                className="img-fluid"
              />
            </a>
          </Col>
          {menuSections(t).map(
            (section: { title: Key | null | undefined; links: any[] }) => (
              <Col xs={24} sm={12} md={8} lg={4} key={section.title}>
                <p className="mb-4 h6">
                  {typeof section.title === "string"
                    ? section.title.toUpperCase()
                    : section.title}
                </p>
                <ul className="list-unstyled" style={{ fontWeight: 400 }}>
                  {section.links.map(
                    (link: { title: Key | null | undefined; url: any }) => (
                      <li key={link.title as Key} className="mb-2">
                        <a
                          href={link.url || "#"}
                          className="text-white text-decoration-none"
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            fontSize: "1rem",
                            color: "#EDF1FB",
                          }}
                        >
                          {link.title}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </Col>
            )
          )}
          <Col xs={24} sm={12} md={8} lg={4}>
            <p className="mb-4 h6">{contactSection(t).title.toUpperCase()}</p>
            <ul className="list-unstyled" style={{ fontWeight: 400 }}>
              <li className="mb-2">
                <b style={{ fontWeight: 500 }}>
                  {contactSection(t).customerServiceHours.label}:
                </b>
                <p style={{ color: "#EDF1FB" }}>
                  {contactSection(t).customerServiceHours.value}
                </p>
              </li>
              <li className="mb-2">
                <b style={{ fontWeight: 500 }}>
                  {contactSection(t).mainPhone.label}
                </b>
                <p>
                  <a
                    href={`tel:${contactSection(t).mainPhone.value}`}
                    className="text-white text-decoration-none"
                    style={{
                      fontSize: "1rem",
                      color: "#EDF1FB",
                    }}
                  >
                    {contactSection(t).mainPhone.value}
                  </a>
                </p>
              </li>
              <li className="mb-2">
                <b style={{ fontWeight: 500 }}>
                  {contactSection(t).forTexasCustomers.label}
                </b>
                <p>
                  <a
                    href={`tel:${contactSection(t).forTexasCustomers.value}`}
                    className="text-white text-decoration-none"
                    style={{
                      fontSize: "1rem",
                      color: "#EDF1FB",
                    }}
                  >
                    {contactSection(t).forTexasCustomers.value}
                  </a>
                </p>
              </li>
            </ul>
          </Col>
        </Row>
        <Row className="pt-3 text-white-50" style={{ fontWeight: "400" }}>
          <Col span={24} className="text-center text-md-left">
            <div className="d-md-flex justify-content-md-start justify-content-center">
              &copy; {new Date().getFullYear()} CleanSky Energy.{" "}
              {t("allRightsReserved")}
            </div>
            <div className="d-flex flex-column flex-md-row align-items-center flex-wrap">
              <div className="d-flex flex-wrap justify-content-center justify-content-md-start me-2">
                {t("licensedIn")}{" "}
                {Object.keys(licenseContent(t)).map((state) => (
                  <button
                    key={state}
                    style={{ fontWeight: 400 }}
                    className={`btn btn-link px-1 p-0 m-0 text-white-50 ${
                      selectedLicense === state
                        ? "text-decoration-underline"
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedLicense(
                        state as keyof ReturnType<typeof licenseContent>
                      )
                    }
                  >
                    <span style={{ fontWeight: 400 }}>{state}</span>
                  </button>
                ))}
              </div>
              <div
                className="mt-2 mt-md-0 text-start"
                style={{ fontSize: "0.8rem" }}
              >
                {selectedLicense && licenseContent(t)[selectedLicense]}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </footer>
  );
};

export default Footer;
